import React, { useMemo } from "react";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { DOB_VALUE_FORMAT, parseDateOfBirth } from "../../utils/dates/dateOfBirth";

// Opening an empty picker on the current year means clicking back four decades
// to reach a real birth year. Start the year grid on a plausible one instead.
const YEARS_BACK_ON_OPEN = 30;

/**
 * DateOfBirthInput — the raw picker behind RffDateOfBirthField. Same DatePicker
 * as DateInput, configured for birth dates: typing is the primary path
 * (MM/DD/YYYY, digits auto-advance through the sections), the calendar opens on
 * a year grid and narrows year → month → day, and future dates are unreachable.
 *
 * Emits a bare "YYYY-MM-DD" string, or "" while the typed date is incomplete or
 * invalid — pass `onError` to distinguish those two cases.
 */
const DateOfBirthInput = ({
  value,
  onChange,
  onBlur,
  onError,
  disabled,
  fullWidth,
  error,
  helperText,
  minDate,
  maxDate,
}) => {
  const dValue = parseDateOfBirth(value);

  const referenceDate = useMemo(
    () => dayjs().startOf("day").subtract(YEARS_BACK_ON_OPEN, "year"),
    [],
  );

  // LENDMARQ DIVERGENCE: see DateInput.jsx — this app has no app-level
  // LocalizationProvider, so each vendored picker carries its own.
  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
    <DatePicker
      value={dValue}
      // Only steers the *initial* view of an empty picker; once a value exists
      // the picker opens on it.
      referenceDate={dValue ? undefined : referenceDate}
      onChange={(next) =>
        onChange(next && next.isValid() ? next.format(DOB_VALUE_FORMAT) : "")
      }
      onError={onError}
      disabled={disabled}
      format="MM/DD/YYYY"
      openTo="year"
      views={["year", "month", "day"]}
      yearsPerRow={3}
      monthsPerRow={3}
      disableFuture
      minDate={minDate}
      maxDate={maxDate}
      slotProps={{
        textField: {
          fullWidth,
          onBlur,
          error,
          helperText,
          size: "small",
          inputProps: { inputMode: "numeric" },
        },
        actionBar: { actions: ["clear", "accept"] },
      }}
    />
    </LocalizationProvider>
  );
};

export default DateOfBirthInput;
